import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  styled,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import { fetchDigitalTwinHierarchy, getHierarchyEntityTypes } from "../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { extractUniqueLevels, fetchDigitalTwinHierarchyLevelSignals, transformTree } from "../Common/CommonFunctions";
import { buildEventsHierarchy } from "../Common/eventsData";
import ChartSeries from "./ChartSeries";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import { usePlatformAlertsAccess } from "../../../Hooks/usePlatformAlertsAccess";
import SignalsTab from "./VisualizationSettingsTab/SignalsTab";
import EventsTab from "./VisualizationSettingsTab/EventsTab";

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontSize: "12px",
  lineHeight: "20px",
  fontWeight: 500,
}));

const CustomTabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: "calc(100% - 50px)" }}
        >
          {value === index && <Box sx={{ p: 3, height: '-webkit-fill-available' }}>{children}</Box>}
        </div>
    );
}

const VisualizationSettings = ({ entities, events, batteryID, onEntitiesChange, onEventsChange = () => {} }) => {

  const {t} = useTranslation(['common', 'digital_twin']);
  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [hierarchyInputData, setHierarchyInputData] = useState({});
  const [eventHierarchyInputData, setEventHierarchyInputData] = useState({});
  const [isLoadingSignals, setIsLoadingSignals] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  const pageContent = usePagesContent();
  const hasSmartAlertsAccess = usePlatformAlertsAccess();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUpdateSelectedEntities = (updatedEntities) => {
    setSelectedEntities(updatedEntities);
  };

  const setupDigitalTwinHierarchy = useCallback(async () => {
    setIsLoadingSignals(true);
    try {
      const entityTypes = getHierarchyEntityTypes(pageContent);
      const digitalTwinHierarchyResponse = await fetchDigitalTwinHierarchy(batteryID, entityTypes, t);
      // fetch signals for unique entities
      const uniqueLevels = extractUniqueLevels(digitalTwinHierarchyResponse ?? []);
      const digitalTwinLevelSignals = await fetchDigitalTwinHierarchyLevelSignals(batteryID, uniqueLevels, 'CONTINUOUS');
      const transformedHierarchy = transformTree(digitalTwinHierarchyResponse ?? [], digitalTwinLevelSignals);
      setHierarchyInputData(transformedHierarchy);
    } finally {
      setIsLoadingSignals(false);
    }
  }, [batteryID, t]);

  const setupEventsHierarchy = useCallback(async () => {
    setIsLoadingEvents(true);
    try {
      const mergedHierarchy = await buildEventsHierarchy({ hasSmartAlertsAccess });
      setEventHierarchyInputData(mergedHierarchy);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [hasSmartAlertsAccess]);

  useEffect(() => {
    setupDigitalTwinHierarchy();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild the events hierarchy when Smart Alerts access resolves so the
  // smartalerts root appears once the permission/feature flag is known.
  useEffect(() => {
    setupEventsHierarchy();
  }, [setupEventsHierarchy]);

  useEffect(() => {
    // Notify parent when entities change
    onEntitiesChange(selectedEntities);
  }, [selectedEntities, onEntitiesChange]);

  useEffect(() => {
    // Notify parent when events change
    onEventsChange(selectedEvents);
  }, [selectedEvents, onEventsChange]);

  useEffect(() => {
    if ((entities || []).length) {
      setSelectedEntities(entities || []);
    }
  }, [entities]);

  useEffect(() => {
    if ((events || []).length) {
      setSelectedEvents(events || []);
    }
  }, [events]);

  return (
    <Box sx={{ width: '350px', borderRadius: '12px', backgroundColor: '#FFFFFF'}}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="signals-series-tab">
          <CustomTab label="Signals" id="continuous-signals" ariaControls="signals" />
          <CustomTab label="Events" id="continuous-events" ariaControls="events"  />
          <CustomTab label="Series" id="continuous-series" ariaControls="series"  />
        </Tabs>
      </Box>
      <CustomTabPanel value={activeTab} index={0}>
        <SignalsTab
          hierarchyInputData={hierarchyInputData}
          batteryID={batteryID}
          selectedEntities={selectedEntities}
          onEntitiesChange={setSelectedEntities}
          isLoading={isLoadingSignals}
        />
      </CustomTabPanel>
      <CustomTabPanel value={activeTab} index={1}>
        <EventsTab
          eventHierarchyInputData={eventHierarchyInputData}
          batteryID={batteryID}
          selectedEvents={selectedEvents}
          onEventsChange={setSelectedEvents}
          isLoading={isLoadingEvents}
        />
      </CustomTabPanel>
      <CustomTabPanel value={activeTab} index={2}>
        <ChartSeries
          selectedEntities={selectedEntities}
          onUpdateEntities={handleUpdateSelectedEntities}
        />
      </CustomTabPanel>
    </Box>
  );
};

export default VisualizationSettings;
